import React, { useState, useEffect } from 'react';
import { EyeIcon, TrashIcon, FunnelIcon } from '@heroicons/react/24/outline';
import Table from '../common/Table';

const DisputesTable = ({
  disputes = [],
  isLoading = false,
  onViewDetails,
  onDelete,
  pagination,
  onPageChange,
  search,
  setSearch,
  statusFilter,
  setStatusFilter,
  dateFiledFilter,
  setDateFiledFilter,
  residencyFilter,
  setResidencyFilter,
}) => {
  // Status badge style mapper
  const getStatusBadgeClass = (status) => {
    const statusMap = {
      pending: 'bg-yellow-100 text-yellow-800',
      scheduled: 'bg-blue-100 text-blue-800',
      settled: 'bg-green-100 text-green-800',
      escalated: 'bg-red-100 text-red-800',
    };

    return `px-2 py-1 rounded-full text-xs font-medium ${
      statusMap[status] || 'bg-gray-100 text-gray-800'
    }`;
  };

  // Date formatter
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  // Local state for debounced input
  const [localSearch, setLocalSearch] = useState(search || '');
  const [filterOpen, setFilterOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [contactConflictDispute, setContactConflictDispute] = useState(null);
  // Debounce: update parent search state after 300ms idle
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearch !== search) setSearch(localSearch);
    }, 300);

    return () => clearTimeout(timer);
  }, [localSearch, setSearch, search]);

  // Table data (inject empty row if needed)
  // ADD this function before: const tableData = disputes.length === 0 ...
  const handleViewClick = (dispute) => {
    const complainantContact = dispute.complainant_contact?.trim();
    const respondentContact = dispute.respondent_contact?.trim();

    if (
      complainantContact &&
      respondentContact &&
      complainantContact === respondentContact
    ) {
      setContactConflictDispute(dispute);
    } else {
      onViewDetails(dispute);
    }
  };
  const tableData =
    disputes.length === 0 ? [{ id: 'empty-row', empty: true }] : disputes;

  // Loading state
  // if (isLoading) {
  //   return (
  //     <div className="flex justify-center items-center h-64">
  //       <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
  //     </div>
  //   );
  // }

  // Table header with input & status filter
  const headerContent = (
    <div>
      <div className='flex justify-between items-center pb-3'>
        <h3 className='text-lg font-medium text-gray-900'>Disputesss</h3>

        <div className='flex items-center gap-2'>
          <div className='relative w-64'>
            <input
              type='text'
              placeholder='Search disputes...'
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              className='w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500'
            />
          </div>

          <button
            onClick={() => setFilterOpen((prev) => !prev)}
            className={`flex items-center gap-1 px-5 py-2 border rounded-lg text-sm transition
            ${
              filterOpen
                ? 'border-blue-500 text-blue-600'
                : 'border-gray-400 text-gray-600 hover:bg-gray-50'
            }`}
          >
            <FunnelIcon
              className={`h-5 w-5 ${
                filterOpen ? 'text-blue-600' : 'text-gray-500'
              }`}
            />
            <span>Filter</span>
          </button>
        </div>
      </div>

      {/* Filter Section */}
      {filterOpen && (
        <div className='pb-3 flex justify-center gap-8 text-sm'>
          {/* Status Filter */}
          <div className='flex flex-col flex-1'>
            <label className='font-semibold text-gray-800 mb-1'>Status</label>
            <select
              value={statusFilter || 'all'}
              onChange={(e) =>
                setStatusFilter(
                  e.target.value === 'all' ? null : e.target.value
                )
              }
              className='w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500'
            >
              <option value='all'>All Status</option>
              <option value='pending'>Pending</option>
              <option value='scheduled'>Scheduled</option>
              <option value='settled'>Settled</option>
              <option value='escalated'>Escalated</option>
            </select>
          </div>

          {/* Date Filed Filter */}
          <div className='flex flex-col flex-1'>
            <label className='font-semibold text-gray-800 mb-1'>
              Date Filed
            </label>
            <input
              type='date'
              value={dateFiledFilter || ''}
              onChange={(e) => setDateFiledFilter(e.target.value || null)}
              className='w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500'
            />
          </div>

          {/* Residency Status Filter */}
          <div className='flex flex-col flex-1'>
            <label className='font-semibold text-gray-800 mb-1'>
              Residency Status
            </label>
            <select
              value={residencyFilter || 'all'}
              onChange={(e) =>
                setResidencyFilter(
                  e.target.value === 'all' ? null : e.target.value
                )
              }
              className='w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500'
            >
              <option value='all'>All Residency</option>
              <option value='resident'>Resident</option>
              <option value='non-resident'>Non-Resident</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );

  // Empty state
  // if (!disputes || disputes.length === 0) {
  //   return (
  //     <div className="flex flex-col justify-center items-center h-64 text-gray-500">
  //       <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
  //         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
  //       </svg>
  //       <p className="text-lg font-medium">No disputes found</p>
  //       <p className="text-sm">Try adjusting your search or filters</p>
  //     </div>
  //   );
  // }

  const columns = [
    {
      key: 'case_number',
      label: 'Case Number',
      render: (_, dispute) =>
        dispute.empty ? (
          <div className='flex flex-col items-center justify-center py-10 text-gray-500 w-full'>
            <p className='text-lg font-medium'>No disputes found</p>
            {/* <p className="text-sm">Try adjusting your search or filters</p> */}
          </div>
        ) : (
          <div className='text-sm font-medium text-gray-900'>
            {dispute.case_number}
          </div>
        ),
    },
    {
      key: 'complainant_name',
      label: 'Complainant',
      render: (_, dispute) =>
        dispute.empty ? null : (
          <>
            <div className='text-sm text-gray-900'>
              {dispute.complainant_name}
            </div>
            <div className='text-xs text-gray-500'>
              {dispute.complainant_type}
            </div>
          </>
        ),
    },
    {
      key: 'respondent_name',
      label: 'Respondent',
      render: (_, dispute) =>
        dispute.empty ? null : (
          <>
            <div className='text-sm text-gray-900'>
              {dispute.respondent_name}
            </div>
            <div className='text-xs text-gray-500'>
              {dispute.respondent_type}
            </div>
          </>
        ),
    },
    {
      key: 'date_filed',
      label: 'Date Filed',
      render: (_, dispute) =>
        dispute.empty ? null : (
          <div className='text-sm text-gray-900'>
            {formatDate(dispute.date_filed)}
          </div>
        ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (_, dispute) =>
        dispute.empty ? null : (
          <span className={getStatusBadgeClass(dispute.status)}>
            {dispute.status?.charAt(0).toUpperCase() +
              dispute.status?.slice(1) || 'Unknown'}
          </span>
        ),
    },
    {
      key: 'next_hearing_date',
      label: 'Next Hearing',
      render: (_, dispute) =>
        dispute.empty ? null : (
          <div className='text-sm text-gray-900'>
            {dispute.next_hearing_date
              ? formatDate(dispute.next_hearing_date)
              : 'Not scheduled'}
          </div>
        ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, dispute) =>
        dispute.empty ? null : (
          <div className='flex justify-end space-x-2'>
            <button
              // onClick={() => onViewDetails(dispute)}
              onClick={() => handleViewClick(dispute)}
              className='text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50'
              title='View Details'
            >
              <EyeIcon className='h-5 w-5' />
            </button>
            <button
              onClick={() => {
                console.log('Delete clicked', dispute);
                onDelete(dispute);
              }}
              className='text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50'
              title='Delete'
            >
              <TrashIcon className='h-5 w-5' />
            </button>
          </div>
        ),
    },
  ];

  return (
    <div>
      {' '}
      <Table
        header={headerContent}
        columns={columns}
        data={tableData}
        getRowKey={(dispute) => dispute.id}
        pagination={pagination}
        onPageChange={onPageChange}
        paginationProps={{ isLoading }}
      />
      {contactConflictDispute && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4'>
          <div className='bg-white rounded-xl shadow-2xl w-full max-w-md animate-fade-in-down'>
            <div className='bg-amber-50 border-b border-amber-100 px-6 py-4 rounded-t-xl flex items-start gap-3'>
              <div className='bg-amber-100 p-2 rounded-full flex-shrink-0'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='h-6 w-6 text-amber-600'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z'
                  />
                </svg>
              </div>
              <div>
                <h3 className='text-base font-semibold text-amber-800'>
                  Duplicate Contact Number Detected
                </h3>
                <p className='text-sm text-amber-600 mt-0.5'>
                  Case #{contactConflictDispute.case_number}
                </p>
              </div>
            </div>

            <div className='px-6 py-5'>
              <p className='text-sm text-gray-700 mb-4'>
                The <span className='font-semibold'>complainant</span> and{' '}
                <span className='font-semibold'>respondent</span> have the same
                contact number:
              </p>

              <div className='bg-gray-50 border border-gray-200 rounded-lg divide-y divide-gray-200 mb-4'>
                <div className='flex items-center justify-between px-4 py-3'>
                  <div>
                    <p className='text-xs text-gray-500 uppercase tracking-wide font-medium'>
                      Complainant
                    </p>
                    <p className='text-sm font-medium text-gray-800'>
                      {contactConflictDispute.complainant_name}
                    </p>
                  </div>
                  <span className='text-sm text-gray-600 bg-white border border-gray-200 px-3 py-1 rounded-full'>
                    {contactConflictDispute.complainant_contact}
                  </span>
                </div>
                <div className='flex items-center justify-between px-4 py-3'>
                  <div>
                    <p className='text-xs text-gray-500 uppercase tracking-wide font-medium'>
                      Respondent
                    </p>
                    <p className='text-sm font-medium text-gray-800'>
                      {contactConflictDispute.respondent_name}
                    </p>
                  </div>
                  <span className='text-sm text-gray-600 bg-white border border-gray-200 px-3 py-1 rounded-full'>
                    {contactConflictDispute.respondent_contact}
                  </span>
                </div>
              </div>

              <p className='text-sm text-gray-500'>
                Please update one of the contact numbers before proceeding. You
                can still view the case details by clicking{' '}
                <span className='font-medium text-gray-700'>"View Anyway"</span>
                .
              </p>
            </div>

            <div className='flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-xl'>
              <button
                onClick={() => setContactConflictDispute(null)}
                className='px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition'
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setContactConflictDispute(null);
                  onViewDetails(contactConflictDispute);
                }}
                className='px-4 py-2 text-sm bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition font-medium'
              >
                View Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(DisputesTable);
